import { createNonce, createSecureHeaders } from "@mcansh/http-helmet";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { data } from "@remix-run/node";
import {
  Outlet,
  ShouldRevalidateFunction,
  useLoaderData,
  useOutletContext,
} from "@remix-run/react";
import CookieConsent from "~/components/CookieConsent";
import Flash from "~/components/Flash";
import Footer from "~/components/Footer";
import GtmIframe from "~/components/GtmIframe";
import HeaderForStore from "~/components/Header/HeaderForStore";
import MarketingMessages from "~/components/MarketingMessages";
import Scripts from "~/components/Scripts";
import { RootLoaderContext, StoreLoaderContext } from "~/entities/global/types";
import useStoreHydration from "~/hooks/useStoreHydration";
import { fetchAll as fetchAllMills } from "~/services/shirtspace/mills.server";
import {
  flashFrom,
  userAndCartFrom,
} from "~/services/shirtspace/session.server";
import { Redirector } from "~/services/unleash/tools.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { user, cart } = await userAndCartFrom(request);
  const flash = flashFrom(request);
  const unleashTest = new Redirector(request, user);
  if (unleashTest.needsRedirection) return unleashTest.redirect();

  const nonce = createNonce();
  const headers = createSecureHeaders({
    "Content-Security-Policy": {
      "default-src": ["'self'"],
      "script-src": [
        "'unsafe-inline'",
        "'https://www.googletagmanager.com'",
        `'nonce-${nonce}'`,
      ],
      "img-src": ["'www.googletagmanager.com'"],
      "connect-src": ["'www.googletagmanager.com'"],
    },
  });

  headers.append("Set-Cookie", `sessionId=${unleashTest.unleashId}; Path=/;`);

  const mills = fetchAllMills();

  return data(
    {
      cart,
      isSignedIn: !!user?.id,
      flash,
      mills,
      user,
      nonce,
    },
    { status: 200, headers },
  );
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  actionResult,
}) => {
  switch (`${actionResult?.name}_${actionResult?.ok}`) {
    case "ApplyStoreCredit_true":
    case "RemoveStoreCredit_true":
    case "EmptyCart_true":
    case "UpdateZip_true":
    case "UpdateCartLineItem_true":
    case "RemoveCartLineItem_true":
    case "UpdateShippingMethod_true":
    case "AddToCart_true": {
      return true;
    }
    default: {
      return false;
    }
  }
};

const Store = () => {
  const {
    flash: cookieFlash,
    cart,
    isSignedIn,
    mills,
    user,
    nonce,
  } = useLoaderData<typeof loader>();
  const context = useOutletContext<RootLoaderContext>();
  const { head } = context;
  const { flash, gtmID } = useStoreHydration({
    cookieFlash,
    user,
    head,
    nonce,
  });

  return (
    <>
      <GtmIframe
        user={user}
        gtmID={gtmID}
      />
      <a
        className="absolute h-0 opacity-0"
        href="#content"
      >
        Skip to content
      </a>
      <HeaderForStore
        cart={cart}
        hasSession={isSignedIn}
        mills={mills}
      />
      <main
        id="content"
        className="relative py-[6.25rem] md:pb-[2.5rem] md:pt-[7.625rem]"
      >
        <MarketingMessages />
        <Flash flash={flash} />
        <Outlet
          context={
            {
              ...context,
              isGTMReady: !!gtmID,
              user,
              cart,
            } satisfies StoreLoaderContext
          }
        />
      </main>
      <Footer />
      <CookieConsent />
      <Scripts
        nonce={nonce}
        user={user}
      />
    </>
  );
};

export default Store;
